/**
 * Shared Monument helpers for the call-summary modals.
 *
 * The voice ([VoiceCallCustomerDetails]) and video ([VideoCallCustomerDetails]) popups
 * remain two separate components (their state plumbing differs too much to merge), but
 * the *pure* Monument logic below was duplicated in both and had already drifted — which
 * is exactly what the silent-note-drop bug rode on. Keeping the payload builders,
 * validation, and the account-id guard here means both modals stay in lock-step.
 */

export type MonumentCallType = "voice" | "video";

export interface MonumentUnitGroup {
    unitGroupUuid: string;
    label: string;
}

export interface MonumentLeadSource {
    leadSourceUuid: string;
    description: string;
}

/**
 * Reduce a phone number to its local digits. Strips a leading US country code (11 digits
 * starting with 1 -> 10). Non-digits are removed entirely.
 */
export const removeCountryCode = (phoneNumber: string): string => {
    const digitsOnly = phoneNumber?.replace(/\D/g, '');
    // If number starts with 1 and has 11 digits, remove the leading 1
    if (digitsOnly?.length === 11 && digitsOnly?.startsWith('1')) {
        return digitsOnly.substring(1);
    }
    return digitsOnly;
};

/**
 * Validates a phone number for display/entry.
 * Accepts digits, spaces, hyphens, parentheses, and plus sign; 10–15 digits.
 */
export const validatePhoneNumber = (phoneNumber: string): { isValid: boolean; error: string } => {
    if (!phoneNumber.trim()) {
        return { isValid: false, error: "Phone number is required" };
    }
    // Characters before length: "must contain at least 10 digits" is nonsense for "wqewe".
    if (!/^[\d\s\-()+ ]+$/.test(phoneNumber)) {
        return { isValid: false, error: "Phone number contains invalid characters" };
    }
    const digitsOnly = phoneNumber?.replace(/\D/g, '');
    if (digitsOnly?.length < 10) {
        return { isValid: false, error: "Phone number must contain at least 10 digits" };
    }
    if (digitsOnly?.length > 15) {
        return { isValid: false, error: "Phone number cannot exceed 15 digits" };
    }
    return { isValid: true, error: "" };
};

/**
 * Validates an email for entry. An empty value is VALID — email is optional (a phone can
 * satisfy Monument's contact requirement instead); a non-empty value must look like an email.
 */
export const validateEmail = (email: string): { isValid: boolean; error: string } => {
    const trimmed = (email || "").trim();
    if (!trimmed) return { isValid: true, error: "" };
    const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    return looksValid ? { isValid: true, error: "" } : { isValid: false, error: "Enter a valid email address" };
};

/**
 * The Monument account id the write-back note targets; "" when the record has none.
 *
 * The single most important guard in this file: the server-side write-back router keys
 * the existing-tenant branch on this id. An empty value matches neither that branch nor
 * the new-lead branch, so the call summary note is silently dropped. Callers MUST refuse
 * to submit (and must NOT mark the modal submitted) when this is empty.
 */
export const getMonumentAccountUuid = (customer: any): string => customer?.AccountUuid || "";

/**
 * The uuid a note written against this record would land on, and which kind of thing it is.
 *
 * A Monument search returns two kinds of customer: an existing tenancy (noted on its
 * account) and an active prospect (noted on its lead). Read the target through here rather
 * than reaching for AccountUuid, which a lead legitimately does not have — treating that
 * absence as "unwritable" is what used to push a known prospect into the new-customer form
 * and create a duplicate lead.
 *
 * Falls back to AccountUuid so a record produced by a backend that predates TargetUuid
 * still resolves.
 */
export const getMonumentTarget = (record: any): { targetType: "tenant" | "lead"; targetUuid: string } => {
    const targetType = record?.TargetType === "lead" ? "lead" : "tenant";
    const targetUuid = record?.TargetUuid || (targetType === "lead" ? record?.LeadUuid : record?.AccountUuid) || "";
    return { targetType, targetUuid };
};

/** True when a record is a prospect rather than an existing tenancy. */
export const isMonumentLead = (record: any): boolean => record?.RecordType === "lead";

/**
 * A distinct Monument tenant an agent may need to choose between for the write-back note.
 * `record` is the underlying normalised record — pass it straight to
 * buildMonumentExistingPayload so the note lands on THIS tenant's account.
 */
export interface MonumentTenant {
    /**
     * The uuid a note attaches to — an account for a tenant, a lead for a prospect.
     * Named accountUuid for history; read `targetType` to know which it is.
     */
    accountUuid: string;
    /** Which Monument object this card writes to. */
    targetType: "tenant" | "lead";
    firstName: string;
    lastName: string;
    phone: string;
    /** Distinct unit numbers across this tenant's rentals, comma-joined; "" when none. */
    unitSummary: string;
    /**
     * Distinct facility names across this tenant's rentals, comma-joined; "" when the
     * backend could not enrich them. The Monument read is PORTFOLIO-WIDE, so two matches
     * can be different people with the same name at different facilities — without this the
     * picker renders them as identical cards and the agent has nothing to choose on.
     */
    facilitySummary: string;
    /**
     * One entry per lease, so a tenant holding several units can say which one the call was
     * about. Empty for a prospect.
     */
    units: MonumentUnitOption[];
    /** Prospect-only display fields; null for a tenancy. */
    lead: {
        status: string;
        source: string;
        enquiredDate: string;
        facilityName: string;
    } | null;
    /** The normalised record for this tenant — the write-back payload source. */
    record: any;
}

/** One of a tenant's leases, as offered in the picker's unit chips. */
export interface MonumentUnitOption {
    unitNumber: string;
    facilityName: string;
    /** What the note is stamped with when this unit is chosen. */
    label: string;
}

/**
 * Collapse a raw Monument read into the DISTINCT tenants the agent may need to pick between.
 *
 * Monument's write-back note is ACCOUNT-level — it is keyed on AccountUuid (see
 * getMonumentAccountUuid / buildMonumentExistingPayload). The backend already returns one
 * normalised record per tenant (grouped by tenantUuid), but the modals used to MERGE every
 * record into record[0], so when one phone/name matched more than one Monument account the
 * note always landed on the FIRST account — a silent cross-account misattribution. This
 * helper keeps the tenants separate so the agent can choose which account the summary
 * attaches to.
 *
 * Records with no AccountUuid are dropped: without one there is no write-back target, so they
 * are not selectable (submitting one would be silently dropped server-side). Deduped by
 * AccountUuid defensively — a name search and a phone search can surface the same account.
 */
export const getMonumentTenants = (records: any[]): MonumentTenant[] => {
    if (!Array.isArray(records)) return [];
    const byTarget = new Map<string, MonumentTenant>();
    for (const record of records) {
        const { targetType, targetUuid } = getMonumentTarget(record);
        if (!targetUuid || byTarget.has(targetUuid)) continue;
        // Rentals is normally an array from the backend, but guard against a malformed
        // (truthy non-array) value so one bad record is skipped rather than throwing — this
        // runs inside the voice open-effect, where an uncaught throw would break the modal.
        const rentals = Array.isArray(record?.Rentals) ? record.Rentals : [];
        const distinct = (pick: (r: any) => any) => Array.from(new Set(
            rentals
                .map(pick)
                .filter((v: any) => v !== undefined && v !== null && String(v).trim() !== "")
                .map((v: any) => String(v))
        ));
        // One option per lease, deduped by unit+facility. Monument's note attaches to the
        // ACCOUNT, so when a tenant holds several units the note text is the only place the
        // unit can be recorded — these are what the agent picks between.
        const units: MonumentUnitOption[] = [];
        const seenUnits = new Set<string>();
        for (const r of rentals) {
            const unitNumber = r?.UnitNumber ? String(r.UnitNumber).trim() : "";
            if (!unitNumber) continue;
            const facilityName = r?.FacilityName ? String(r.FacilityName).trim() : "";
            const key = `${unitNumber}|${facilityName}`;
            if (seenUnits.has(key)) continue;
            seenUnits.add(key);
            units.push({
                unitNumber,
                facilityName,
                label: facilityName ? `Unit ${unitNumber} — ${facilityName}` : `Unit ${unitNumber}`,
            });
        }
        const leadBlock = record?.Lead || null;
        byTarget.set(targetUuid, {
            accountUuid: targetUuid,
            targetType,
            firstName: record?.FirstName || "",
            lastName: record?.LastName || "",
            phone: record?.Phone ? String(record.Phone) : "",
            unitSummary: distinct((r: any) => r?.UnitNumber).join(", "),
            // FacilityName is null on every rental when the backend's facility enrichment
            // returned nothing; that yields "" and the picker just omits the line.
            facilitySummary: distinct((r: any) => r?.FacilityName).join(", "),
            units,
            lead: targetType === "lead"
                ? {
                    status: leadBlock?.LeadStatus || "",
                    source: leadBlock?.LeadSource || "",
                    enquiredDate: leadBlock?.EnquiredDate || "",
                    facilityName: leadBlock?.FacilityName || "",
                }
                : null,
            record,
        });
    }
    return Array.from(byTarget.values());
};

/**
 * Split the matches into the two sections the picker renders.
 *
 * Tenancies come first because the backend orders them first and the default selection —
 * which the call-end auto-submit uses when the agent chose nothing — should be an existing
 * tenancy rather than an enquiry.
 */
export const getMonumentTargets = (records: any[]): { tenants: MonumentTenant[]; leads: MonumentTenant[] } => {
    const all = getMonumentTenants(records);
    return {
        tenants: all.filter((t) => t.targetType === "tenant"),
        leads: all.filter((t) => t.targetType === "lead"),
    };
};

/**
 * Did the Monument read resolve anything the agent can attach a note to?
 *
 * The SSM-shaped modals decide "nothing found" by looking for Inquiries / Reservations /
 * Rentals. Monument records have none of those collections, and a PROSPECT legitimately has
 * an empty Rentals — so those checks read every lead as an empty result, which showed a
 * "no customer found, add a new customer" box for someone Monument already had and, on the
 * phone-search path, dropped the agent into the new-customer form. For Monument the only
 * meaningful question is whether a write target came back.
 */
export const hasMonumentMatch = (records: any[]): boolean => {
    const { tenants, leads } = getMonumentTargets(records);
    return tenants.length + leads.length > 0;
};

/**
 * The unit text stamped at the top of a tenant's call-summary note.
 *
 * Monument notes attach to the account, never to a lease, so this line is the only record
 * of which unit the call concerned. When the agent picks a unit we name exactly that one;
 * when they don't, we name them ALL rather than guessing — the previous behaviour stamped
 * whichever lease the search happened to return first, which reads as a confident, wrong
 * answer to someone reading the note later. Facility is included because unit numbers
 * repeat across facilities and an account can span them.
 */
export const buildMonumentUnitLabel = (tenant: MonumentTenant | null, selectedUnitLabel: string): string => {
    if (!tenant || tenant.targetType !== "tenant") return "";
    const units = tenant.units || [];
    if (selectedUnitLabel && units.some((u) => u.label === selectedUnitLabel)) return selectedUnitLabel;
    if (!units.length) return "";
    if (units.length === 1) return units[0].label;
    return `Units: ${units.map((u) => (u.facilityName ? `${u.unitNumber} (${u.facilityName})` : u.unitNumber)).join(", ")}`;
};

export interface MonumentNewLeadFields {
    firstName: string;
    lastName: string;
    /** Raw phone as typed. Optional — Monument needs a phone OR an email, not both. */
    phone: string;
    /** Raw email as typed. Optional — Monument needs a phone OR an email, not both. */
    email: string;
}

/**
 * Manual-submit validation for a Monument new lead — the set create-lead actually enforces
 * (live-probed): names, and at least ONE contact method (it 400s with "person.Either email
 * or phone must be provided" when both are missing). Address and unit group are NOT required
 * — Monument made unitGroupUuid optional on 2026-08-27, and the docs mark addressLine1/2
 * required though the API accepts a lead without them.
 * Returns an errors map (empty = valid); keys match the modals' error state.
 */
export const validateMonumentNewLead = (
    f: MonumentNewLeadFields
): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    if (!f.firstName.trim()) errors.firstName = "First Name is required";
    if (!f.lastName.trim()) errors.lastName = "Last Name is required";
    // Monument needs a phone OR an email (each individually optional). Require at least one,
    // and validate whichever the agent actually entered.
    const phone = (f.phone || "").trim();
    const email = (f.email || "").trim();
    if (!phone && !email) {
        errors.contact = "Enter a phone number or an email";
    } else {
        if (phone) {
            const phoneValidation = validatePhoneNumber(phone);
            if (!phoneValidation.isValid) errors.phoneNumber = phoneValidation.error;
        }
        if (email) {
            const emailValidation = validateEmail(email);
            if (!emailValidation.isValid) errors.email = emailValidation.error;
        }
    }
    return errors;
};

/**
 * Auto-submit gate for a Monument new lead.
 *
 * Monument HARD-requires firstName + lastName + (email or phone) for createLead to
 * succeed. Address and unit group are optional at runtime, so neither is gated here — an
 * auto-submit may proceed without them. When this returns false the caller
 * must SKIP the write (and must NOT mark the modal submitted) rather than POST a payload the
 * backend would silently refuse, which would otherwise read as a false success.
 */
export const monumentNewLeadCanAutoSubmit = (f: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
}): boolean =>
    !!(f.firstName.trim() && f.lastName.trim() &&
        ((f.phone || "").trim() || (f.email || "").trim()));

export interface BuildMonumentNewLeadArgs {
    tenantDetails: { firstName: string; lastName: string };
    unitGroupUuid?: string;
    /** Resolved phone (already stripped of country code by the caller). Optional — email may substitute. */
    phone: string;
    /** Email as entered. Optional — phone may substitute. */
    email?: string;
    callSid: string | undefined;
    locationId: string | undefined;
    agentName: string;
    callType: MonumentCallType;
    leadSourceUuid?: string;
    addressLine1?: string;
    addressLine2?: string;
}

/**
 * Builds the Monument new-lead payload. Monument needs firstName/lastName + a contact
 * (phone OR email); the SSM fields
 * (dateNeeded/advSource/countryId/facilityId/tenantId) are omitted. Absent optional keys
 * are omitted entirely — never sent as null — because the backend flattens with
 * String(value) and would otherwise misread "null" as a real value.
 */
export const buildMonumentNewLeadPayload = (a: BuildMonumentNewLeadArgs): Record<string, any> => {
    const payload: Record<string, any> = {
        firstName: a.tenantDetails.firstName.trim(),
        lastName: a.tenantDetails.lastName.trim(),
        phone: a.phone || "",
        customerType: "new",
        callType: a.callType,
        callSid: a.callSid,
        locationId: a.locationId,
        agentName: a.agentName,
        // Marks the payload as Monument's even when every optional id is absent. Since
        // unitGroupUuid became optional a names-only lead carries no Monument id at all, and
        // the write-back router would misroute it into SSM (see MONUMENT_WRITE_MARKERS).
        pmsProvider: "monument",
    };
    // Optional email — Monument accepts phone OR email. Sent only when present, never as an
    // empty string (the backend flattens with String(value), so "" would arrive as noise).
    if (a.email && a.email.trim()) payload.email = a.email.trim();
    // Optional since 2026-08-27. Must be OMITTED rather than blanked: Monument 400s on
    // unitGroupUuid:"" ("should not be empty") but accepts the key being absent.
    if (a.unitGroupUuid) payload.unitGroupUuid = a.unitGroupUuid;
    // Optional lead-source attribution — omitted when the agent didn't pick one.
    if (a.leadSourceUuid) payload.leadSourceUuid = a.leadSourceUuid;
    // Monument's docs mark addressLine1/2 required (the live runtime does not enforce
    // them). Sent when present; omitted (never null) otherwise, matching every other key.
    if (a.addressLine1) payload.addressLine1 = a.addressLine1;
    if (a.addressLine2) payload.addressLine2 = a.addressLine2;
    return payload;
};

/**
 * Builds the Monument existing-tenant payload — just the account id + the unit the call
 * concerned. The note itself is the auto-generated call summary (server-side), so the
 * agent types nothing. tenantUuid must be present (see getMonumentAccountUuid) or the
 * backend cannot target the note.
 */
export const buildMonumentExistingPayload = (
    customer: any,
    callSid: string | undefined,
    locationId: string | undefined,
    agentName: string,
    callType: MonumentCallType,
    /** Which unit the agent said the call was about; "" means "don't narrow it". */
    unitLabel: string = ""
): Record<string, any> => {
    const { targetType, targetUuid } = getMonumentTarget(customer);
    const base: Record<string, any> = {
        // What the agent selected. The server routes on this: a lead is noted on the lead,
        // a tenancy on its account. Sent explicitly so neither is inferred from which id
        // happens to be present.
        targetType,
        targetUuid,
        customerType: "existing",
        callType,
        callSid,
        locationId,
        agentName,
        // Same provider marker as the new-lead payload — keeps write-back routing keyed on
        // the provider rather than on whichever id happens to be present.
        pmsProvider: "monument",
        customerFirstName: customer?.FirstName || "",
        customerLastName: customer?.LastName || "",
    };
    if (targetType === "lead") {
        base.leadUuid = targetUuid;
        return base;
    }
    // tenantUuid and unitNumber are the pre-targetType field names. Kept so a server that
    // has not picked up the new routing yet still writes this note to the right account.
    base.tenantUuid = targetUuid;
    base.unitNumber = customer?.Rentals?.[0]?.UnitNumber || "";
    if (unitLabel) base.unitLabel = unitLabel;
    return base;
};

/**
 * A Monument date as "26 Aug 2026", rendered in UTC.
 *
 * UTC deliberately, not the viewer's timezone. Monument sends instants
 * ("2025-08-25T05:00:00.000Z") that represent a facility-local business date, so rendering
 * them locally shifts the day for anyone far enough west — 05:00Z is the 24th, not the 25th,
 * for an agent on US Central. The date an agent reads has to match the date Monument shows,
 * and UTC is the only rendering that is the same for every agent.
 *
 * Falls back to the raw value rather than printing "Invalid Date", and returns " - " for a
 * missing date so a caller can render it without its own guard.
 *
 * Shared by the in-call lead panel and the call-summary picker. It lived in both files and
 * the copies had already drifted (one had the empty-input guard, the other did not).
 */
export const formatMonumentDate = (raw?: string | null): string => {
    if (!raw) return " - ";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
    });
};
